import { useState } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import { useFHIRTokens } from '../../hooks/useFHIRTokens';
import { isMockAuthenticatorEnabled } from '../../utils/devToggles';
import SettingsToggle from './SettingsToggle';
import './IgnoredUsersPage.css';
import './VzdDirectoryPage.css';

// Map each in-progress step to an i18n key
const STEP_LABEL_KEYS = {
  challenge:  'FHIRAccount.stepChallenge',
  waiting:    'FHIRAccount.stepWaiting',
  exchanging: 'FHIRAccount.stepExchanging',
  fhir:       'FHIRAccount.stepFhir',
  visibility: 'FHIRAccount.stepVisibility',
};

const STEP_LABEL_DEFAULTS = {
  challenge:  'Connecting to authenticator…',
  waiting:    'Waiting for smartcard authentication…',
  exchanging: 'Verifying credentials…',
  fhir:       'Fetching FHIR token…',
  visibility: 'Loading directory settings…',
};

const FHIRAccountPage = ({ onBack }) => {
  const { t } = useI18n();
  const { state, fetchTokens, setVisibility, setInsureeVisibility } = useFHIRTokens();
  const { step, error, isRegistered, isGenerallyVisible, isVisibleToInsurees, isUpdating, updateError } = state;
  const [mockAuth] = useState(isMockAuthenticatorEnabled);

  const isLoading = step !== 'idle' && step !== 'ready' && step !== 'error';
  const isReady = step === 'ready';

  const stepLabel = isLoading
    ? t(STEP_LABEL_KEYS[step], {}, STEP_LABEL_DEFAULTS[step])
    : null;

  return (
    <div className="settings-page-container vzd-directory-page">
      <div className="settings-modal-header">
        <button className="back-button" onClick={onBack} aria-label={t('FHIRAccount.goBack', {}, 'Go back to settings')}>
          <ion-icon name="chevron-back-outline" />
        </button>
        <span className="settings-page-title">{t('VzdDirectory.pageTitle')}</span>
      </div>

      <div className="settings-page-content">
        <div className="vzd-directory-content">
          <div className="settings-sections">
        {mockAuth && (
          <div className="settings-section">
            <div className="settings-section-content">
              <div className="vzd-status-box info">
                <ion-icon name="flask-outline" />
                <div className="status-text">
                  <div className="status-title">
                    {t('FHIRAccount.mockAuthBannerTitle', {}, 'Mock Authenticator: ENABLED')}
                  </div>
                  <div className="status-description">
                    {t('FHIRAccount.mockAuthBannerDescription', {}, 'Credential strength is mocked. Disable in Developer Info before validating real flows.')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Loading state — the flow auto-starts on page entry */}
        {isLoading && (
          <div className="settings-section">
            <div className="settings-section-content">
              <div className="vzd-status-box info">
                <ion-icon name="sync-outline" class="vzd-loading-spinner" />
                <div className="status-text">
                  <div className="status-description">{stepLabel}</div>
                </div>
              </div>

              {/* Extra hint while waiting for the user to complete smartcard auth */}
              {step === 'waiting' && (
                <div className="vzd-status-box info" style={{ marginTop: 8 }}>
                  <ion-icon name="card-outline" />
                  <div className="status-text">
                    <div className="status-description">
                      {t('FHIRAccount.waitingHint', {}, 'Please complete the authentication in the authenticator app, then return here.')}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error state */}
        {step === 'error' && (
          <div className="settings-section">
            <div className="settings-section-content">
              <div className="vzd-status-box warning">
                <ion-icon name="alert-circle-outline" />
                <div className="status-text">
                  <div className="status-title">
                    {t('FHIRAccount.loadError', {}, 'Failed to load FHIR account data.')}
                  </div>
                  <div className="status-description">{error}</div>
                </div>
                <button className="vzd-retry-button" onClick={fetchTokens}>
                  {t('FHIRAccount.retryButton', {}, 'Retry')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Visibility settings — shown once data is loaded */}
        {isReady && (
          <>
            {!isRegistered && (
              <div className="settings-section">
                <div className="settings-section-content">
                  <div className="vzd-status-box info">
                    <ion-icon name="information-circle-outline" />
                    <div className="status-text">
                      <div className="status-description">
                        {t('FHIRAccount.notRegistered', {}, 'Your account is not registered in the healthcare directory.')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isRegistered && (
              <div className="settings-section">
                <div className="settings-section-header">
                  {t('VzdDirectory.sectionTitle')}
                </div>
                <div className="settings-section-content vzd-toggles">
                  <SettingsToggle
                    iconSrc="/images/svg/healthcare-address-book.svg"
                    label={t('VzdDirectory.searchableToggleLabel')}
                    description={t('VzdDirectory.searchableToggleDescription')}
                    checked={isGenerallyVisible}
                    onChange={setVisibility}
                    disabled={isUpdating}
                  />
                  <SettingsToggle
                    iconSrc="/images/svg/healthcare-check.svg"
                    label={t('VzdDirectory.insureeVisibilityLabel')}
                    description={t('VzdDirectory.insureeVisibilityDescription')}
                    checked={isVisibleToInsurees}
                    onChange={setInsureeVisibility}
                    disabled={isUpdating || !isGenerallyVisible}
                  />
                </div>
                {isUpdating && (
                  <div className="vzd-updating-indicator">
                    <ion-icon name="sync-outline" class="vzd-loading-spinner" />
                    <span>{t('VzdDirectory.updatingVisibility')}</span>
                  </div>
                )}
              </div>
            )}

            {updateError && (
              <div className="settings-section">
                <div className="settings-section-content">
                  <div className="vzd-status-box warning">
                    <ion-icon name="alert-circle-outline" />
                    <div className="status-text">
                      <div className="status-title">
                        {t('FHIRAccount.updateError', {}, 'Failed to update visibility.')}
                      </div>
                      <div className="status-description">{updateError}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="settings-section">
              <div className="settings-section-content">
                <button
                  className="settings-line-button"
                  onClick={fetchTokens}
                  disabled={isUpdating}
                >
                  <div className="settings-line-button-content">
                    <ion-icon name="refresh-outline" class="settings-line-icon-ionicon" />
                    <span>{t('FHIRAccount.refreshButton', {}, 'Refresh')}</span>
                  </div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

FHIRAccountPage.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default FHIRAccountPage;
